import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useLanguage, LangToggle, categoryIcons } from '../context/LanguageContext'
import { useSubscription } from '../context/SubscriptionContext'
import WelcomeModal from '../components/WelcomeModal'

function getDaysLeft(expiryDate) {
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
}

function getBadge(days, t) {
  if (days < 0) return { label: t.expired, cls: 'badge-expired' }
  if (days <= 30) return { label: t.badgeDays(days), cls: 'badge-warning' }
  return { label: t.active, cls: 'badge-active' }
}

function getDaysLabel(days, t) {
  if (days < 0) return null
  if (days === 0) return t.expiresToday
  if (days <= 30) return null
  if (days <= 365) return t.daysLeft(days)
  const years = Math.floor(days / 365)
  const months = Math.floor((days % 365) / 30)
  return t.yearsLeft(years, months)
}

export default function Dashboard() {
  const { user } = useAuth()
  const { t, lang, translateCategory } = useLanguage()
  const { plan, canAddWarranty } = useSubscription()
  const [warranties, setWarranties] = useState([])
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('damanati_welcome_seen'))
  const navigate = useNavigate()

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const [{ data: warranties }, { data: profile }] = await Promise.all([
      supabase.from('warranties').select('*').eq('user_id', user.id).order('expiry_date', { ascending: true }),
      supabase.from('profiles').select('*').eq('id', user.id).single()
    ])
    setWarranties(warranties || [])
    setProfile(profile)
    setLoading(false)
  }

  const active       = warranties.filter(w => getDaysLeft(w.expiry_date) >= 0).length
  const expiringSoon = warranties.filter(w => { const d = getDaysLeft(w.expiry_date); return d >= 0 && d <= 30 }).length
  const expired      = warranties.filter(w => getDaysLeft(w.expiry_date) < 0).length
  const firstName    = profile?.full_name?.split(' ')[0] || t.greetingFallback

  return (
    <div className="app-layout">
      <div className="topbar">
        <button className="topbar-btn" onClick={() => navigate('/profile')} aria-label={t.profileLabel || 'Profile'}>👤</button>
        <div className="topbar-logo">
          <span style={{ color: '#E8610A', fontWeight: 900, fontSize: '1.1rem' }}>{t.appName} 🛡️</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <LangToggle />
          <button className="topbar-btn" onClick={() => navigate('/add')} aria-label={t.addWarrantyTitle || 'Add warranty'}>＋</button>
        </div>
      </div>

      <div className="main-content">
        <div className="greeting">
          <div className="greeting-hi">{t.hello}</div>
          <div className="greeting-name">{firstName} 👋 {plan === 'premium' && <span className="premium-badge">{t.premiumBadge}</span>}</div>
        </div>

        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card-num" style={{ color: '#ff8080' }}>{expired}</div>
            <div className="stat-card-lbl">{t.expired}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num" style={{ color: '#FBBF24' }}>{expiringSoon}</div>
            <div className="stat-card-lbl">{t.expiringSoon}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num">{active}</div>
            <div className="stat-card-lbl">{t.active}</div>
          </div>
        </div>

        <div className="section-title">
          <span>{t.myWarranties} ({warranties.length})</span>
        </div>

        {loading ? (
          <div className="loading">{t.loading}</div>
        ) : warranties.length === 0 ? (
          <div className="empty-state onboarding-empty">
            <div className="onboarding-illustration">
              <div className="onboarding-shield">🛡️</div>
              <div className="onboarding-pulse" />
            </div>
            <div className="empty-title">{t.noWarranties}</div>
            <div className="empty-sub">{t.noWarrantiesSub}</div>
            <button className="btn-full onboarding-cta" onClick={() => navigate('/add')}>
              {t.addWarrantyFab}
            </button>
          </div>
        ) : (
          <div className="warranty-list">
            {warranties.map(w => {
              const days     = getDaysLeft(w.expiry_date)
              const badge    = getBadge(days, t)
              const daysLabel = getDaysLabel(days, t)
              const displayCategory = translateCategory(w.category)
              return (
                <Link key={w.id} to={`/warranty/${w.id}`} className="warranty-card">
                  <div className="wc-icon" role="img" aria-label={displayCategory}>{categoryIcons[w.category] || '📦'}</div>
                  <div className="wc-info">
                    <div className="wc-name">{w.product_name}</div>
                    <div className="wc-brand">{w.brand || displayCategory}</div>
                  </div>
                  <div className="wc-right">
                    <span className={`badge ${badge.cls}`}>{badge.label}</span>
                    <div className="wc-exp">{new Date(w.expiry_date).toLocaleDateString()}</div>
                    {daysLabel && (
                      <div className="wc-days" style={{ color: 'var(--ash)', fontSize: '0.68rem', marginTop: 2 }}>
                        {daysLabel}
                      </div>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      <button className="fab" onClick={() => navigate(canAddWarranty ? '/add' : '/pricing')}>
        {canAddWarranty ? t.addWarrantyFab : t.upgradeNow}
      </button>

      {showWelcome && <WelcomeModal lang={lang} onClose={() => setShowWelcome(false)} />}
    </div>
  )
}
